import { Pencil, Trash2, XCircle } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { useAuth } from "../Store/auth-context";
import { Schein } from "../Store/interfaces";
import { useScheinContext } from "../Store/schein-context";
import { useUIContext } from "../Store/ui-context";
import PDFViewer from "./PdfViewer";
import CustomButton from "./Ui/CustomButton";

interface HistorieProps {}

const Historie: FC<HistorieProps> = () => {
  const [isSending, setIsSending] = useState(false);
  const [scheine, setScheine] = useState<Schein[]>([]);
  const [showPDF, setShowPDF] = useState<boolean>(false);
  const [currentPdf, setCurrentPdf] = useState<string>("");

  const [localScheine, setLocalScheine] = useState<any[]>([]);
  const [onlineScheine, setOnlineScheine] = useState<Schein[]>([]);
  const [loadAllOnline, setLoadAllOnline] = useState<boolean>(false);

  const { setShowHistorie } = useUIContext();
  const {
    setSelectedCustomer,
    setComment,
    setDay,
    setFahrtBerechnen,
    setStartOrt,
    setZielOrt,
    setStartTime,
    setEndTime,
    setSelectedProducts,
  } = useScheinContext();
  const { setCurrentPage } = useUIContext();

  const { token } = useAuth();

  useEffect(() => {
    console.log("try to get all Scheins");
    retrieveAllScheins();

    const fetchScheinNrVonBearbeiter = async () => {
      if (isSending) return;

      setIsSending(true);
      try {
        const url = `https://qas.freyadv.de/eserviceschein/snrs/bearbeiter/NP`; //${name}
        const response = await fetch(url, {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin":
              "http://localhost:3000/serviceschein",
          },
        });

        if (response.status !== 200) {
          alert("Ungültige Eingabe, bitte überprüfen.");
          setIsSending(false);
          return;
        }

        const resData = await response.json();
        console.log("fetch snr of bearbeiter response", resData);

        if (resData && resData.length > 0) {
          // Fetch all the Schein objects first to sort them by date
          const scheinObjects: Schein[] = [];
          for (const snr of resData) {
            const schein = await fetchScheinObjekt(snr);
            if (schein) {
              scheinObjects.push(schein);
            }
          }

          setScheine(scheinObjects);

          if (!loadAllOnline) {
            // Sort the objects by date in descending order and take the top 10
            const sortedScheine = scheinObjects
              .sort(
                (a, b) =>
                  new Date(b.datum).getTime() - new Date(a.datum).getTime()
              )
              .slice(0, 5);

            // Set the state with the top 10 sorted objects
            setOnlineScheine(sortedScheine);
          } else {
            setOnlineScheine(scheinObjects);
          }
        } else {
          alert("Keine Scheine ermittelt.");
        }
      } catch (error) {
        console.error(error);
        alert(error);
      } finally {
        setIsSending(false);
      }
    };

    const fetchScheinObjekt = async (snr: string): Promise<Schein | null> => {
      try {
        const url = `https://qas.freyadv.de/eserviceschein/snrs/${snr}`;
        const response = await fetch(url, {
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin":
              "http://localhost:3000/serviceschein",
          },
        });

        if (response.status !== 200) {
          alert("Ungültige Eingabe, bitte überprüfen.");
          return null;
        }
        const schein: Schein = await response.json();
        return schein;
      } catch (error) {
        console.error(error);
        alert(error);
        return null;
      }
    };

    fetchScheinNrVonBearbeiter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retrieveAllScheins = () => {
    if (localScheine.length === 0) {
      const scheins = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith("schein_")) {
          const schein = JSON.parse(localStorage.getItem(key)!); // Use non-null assertion since we check for null
          const id = key.split("_")[1]; // Extract the ID from the key
          const kundeKey = `kunde_${id}`;
          const kunde = JSON.parse(localStorage.getItem(kundeKey)!); // Retrieve the customer object using the extracted ID

          // Combine schein and kunde objects
          const combinedSchein = { ...schein, kunde };
          scheins.push(combinedSchein);
        }
      }
      console.log("lokale scheine: " + scheins.length);
      setLocalScheine(scheins);
    }
  };

  function onScheinClick(pdf: string) {
    setCurrentPdf(pdf);
    setShowPDF(true);
  }

  function hidePdf() {
    setShowPDF(false);
  }

  function deleteLocalSchein(snr: string) {
    localStorage.removeItem("schein_" + snr);
    let locals = localScheine.filter((sch) => sch.snr !== snr);

    setLocalScheine(locals);
  }

  function loadLocalSchein(snr: string) {
    console.log("load local");
    const sch = localScheine.filter((sch) => sch.snr === snr)[0];
    if (sch) {
      console.log("local gefunden", sch);
      setSelectedProducts(sch.pos);
      setComment(sch.kommentar);
      setStartOrt(sch.startort);
      setZielOrt(sch.zielort);

      setStartTime(sch.startTime);
      setEndTime(sch.endTime);

      setSelectedCustomer(sch.kunde);

      setDay(new Date(sch.datum));
      setFahrtBerechnen(sch.fahrtberechnen);
      setCurrentPage("info");
      setShowHistorie(false);
    } else {
      alert("Schein konnte nicht geladen werden.");
    }
  }

  function toggleLoadMore() {
    if (loadAllOnline) {
      const sortedScheine = scheine
        .sort(
          (a, b) => new Date(b.datum).getTime() - new Date(a.datum).getTime()
        )
        .slice(0, 5);

      // Set the state with the top 10 sorted objects
      setOnlineScheine(sortedScheine);
    } else {
      setOnlineScheine(scheine);
    }

    setLoadAllOnline(!loadAllOnline);
  }

  return (
    <>
      {showPDF && <PDFViewer onClose={hidePdf} base64Pdf={currentPdf} />}
      {!showPDF && (
        <div
          className="select-none flex flex-col items-center justify-between w-[80%] mx-auto rounded-2xl bg-white p-6"
          style={{ minHeight: "100vh" }}
        >
          <div className="flex justify-between w-1/2">
            <label className="select-none col-span-2 w-full text-2xl font-medium leading-6 text-center text-blue_royal">
              Online-Scheine einsehen
            </label>
            <button
              type="button"
              className="pb-4"
              onClick={() => {
                setShowHistorie(false);
              }}
            >
              <XCircle size={24}></XCircle>
            </button>
          </div>

          <table className="table-auto w-1/2 mt-6">
            <thead>
              <tr>
                <th className="px-4 py-2 text-blue_royal font-sans">
                  Kundennr.
                </th>
                <th className="px-4 py-2 text-blue_royal font-sans">Datum</th>
                <th className="px-4 py-2 text-blue_royal font-sans">
                  Kommentar
                </th>
                <th className="px-4 py-2 text-blue_royal font-sans">
                  Produktanzahl
                </th>
                <th className="px-4 py-2 text-blue_royal font-sans">
                  Scheinnr.
                </th>
              </tr>
            </thead>
            <tbody>
              {onlineScheine.map((schein, index) => (
                <tr
                  key={index}
                  className={`border-t-2 ${
                    schein.pdf
                      ? "bg-lightgray cursor-pointer hover:bg-gray-200"
                      : "bg-gray-300"
                  }`}
                  onClick={() => onScheinClick(schein.pdf)}
                >
                  <td className="px-4 py-2">{schein.kdnr}</td>
                  <td className="px-4 py-2">
                    {new Date(schein.datum).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-center">{schein.kommentar}</td>
                  <td className="px-4 py-2 text-center">
                    {schein.pos?.length ?? "0"}
                  </td>
                  <td className="px-4 py-2">{schein.snr}</td>
                  <td className="px-4 py-2 text-center text-red">
                    {schein.pdf ? "" : "Keine PDF vorhanden"}
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={6} className="px-4 py-2 text-center col-span-6">
                  <CustomButton theme="navy" onClick={toggleLoadMore}>
                    {!loadAllOnline ? "Alle laden" : "Weniger laden"}
                  </CustomButton>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="flex mt-5 justify-between w-1/2 border-t-2 border-b-slate-500">
            <label className="select-none col-span-2 w-full text-2xl font-medium leading-6 text-center mt-10 text-blue_royal">
              Lokale Scheine bearbeiten
            </label>
          </div>
          <table className="table-auto w-1/2 mt-6">
            <thead>
              <tr>
                <th className="px-4 py-2 text-blue_royal font-sans">
                  Kundennr.
                </th>
                <th className="px-4 py-2 text-blue_royal font-sans">Datum</th>
                <th className="px-4 py-2 text-blue_royal font-sans">
                  Kommentar
                </th>
                <th className="px-4 py-2 text-blue_royal font-sans">
                  Produktanzahl
                </th>
                <th className="px-4 py-2 text-blue_royal font-sans">
                  Scheinnr.
                </th>
                <th className="px-4 py-2 text-blue_royal font-sans">Löschen</th>
                <th className="px-4 py-2 text-blue_royal font-sans">
                  Aufrufen
                </th>
              </tr>
            </thead>
            <tbody>
              {localScheine.map((schein, index) => (
                <tr
                  key={index}
                  className="border-t-2 bg-lightgray cursor-pointer hover:bg-gray-200"
                >
                  <td className="px-4 py-2">{schein.kdnr}</td>
                  <td className="px-4 py-2">
                    {new Date(schein.datum).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-center">{schein.kommentar}</td>
                  <td className="px-4 py-2  text-center">
                    {schein.pos?.length ?? "0"}
                  </td>
                  <td className="px-4 py-2">{schein.snr}</td>
                  <td>
                    <button
                      className="mx-auto"
                      onClick={() => {
                        deleteLocalSchein(schein.snr);
                      }}
                    >
                      <Trash2 size={24} />
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => loadLocalSchein(schein.snr)}
                      className="mx-auto"
                    >
                      <Pencil size={24} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default Historie;
